import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import platformService from '../services/platformService';

// Get all platforms with smart caching
export const getPlatforms = createAsyncThunk(
  'platforms/getPlatforms',
  async (forceRefresh = false, { getState, rejectWithValue }) => {
    try {
      const state = getState().platforms;
      const now = Date.now();

      // Temporary logging to verify caching
      console.log('🔍 getPlatforms called with forceRefresh:', forceRefresh);
      console.log('📊 Platforms cache status:', {
        cached: state.cached,
        lastFetched: state.lastFetched,
        age: state.lastFetched ? (now - state.lastFetched) / 1000 : 'never',
        timeout: state.cacheTimeout / 1000
      });

      // Check if we should use cached data
      if (!forceRefresh && state.cached && state.lastFetched &&
          (now - state.lastFetched) < state.cacheTimeout) {
        console.log('✅ Using cached platforms data');
        // Return cached data with special flag
        return { data: state.platforms, fromCache: true };
      }

      console.log('🌐 Fetching fresh platforms data');
      // Fetch fresh data
      const platforms = await platformService.getAll();
      return platforms;
    } catch (error) {
      console.error('platformsSlice: Error fetching platforms:', error);
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

// Get a single platform
export const getPlatform = createAsyncThunk(
  'platforms/getPlatform',
  async (id, { rejectWithValue }) => {
    try {
      return await platformService.getById(id);
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

// Create a new platform
export const createPlatform = createAsyncThunk(
  'platforms/createPlatform',
  async (platformData, { rejectWithValue }) => {
    try {
      if (process.env.NODE_ENV === 'development') {
        console.log('platformsSlice: Creating platform with data:', platformData);
      }
      const result = await platformService.create(platformData);
      if (process.env.NODE_ENV === 'development') {
        console.log('platformsSlice: Platform created successfully:', result);
      }
      return result;
    } catch (error) {
      console.error('platformsSlice: Error creating platform:', error);

      // Extract the error message
      let errorMessage = error.message;

      // If there's a response with data
      if (error.response?.data) {
        if (process.env.NODE_ENV === 'development') {
          console.error('platformsSlice: Error response data:', error.response.data);
        }

        // Handle different error data formats
        if (typeof error.response.data === 'object') {
          // If it's an object, try to extract the message or error field
          errorMessage = error.response.data.message || error.response.data.error || JSON.stringify(error.response.data);
        } else if (typeof error.response.data === 'string') {
          // If it's a string, use it directly
          errorMessage = error.response.data;
        }
      }

      return rejectWithValue(errorMessage);
    }
  }
);

// Update a platform
export const updatePlatform = createAsyncThunk(
  'platforms/updatePlatform',
  async ({ id, platformData }, { rejectWithValue }) => {
    try {
      return await platformService.update(id, platformData);
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

// Delete a platform
export const deletePlatform = createAsyncThunk(
  'platforms/deletePlatform',
  async (id, { rejectWithValue }) => {
    try {
      await platformService.remove(id);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

const initialState = {
  platforms: [],
  currentPlatform: null,
  loading: false,
  error: null,
  success: false,
  cached: false,
  lastFetched: null,
  cacheTimeout: 5 * 60 * 1000, // 5 minutes
};

const platformsSlice = createSlice({
  name: 'platforms',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearSuccess: (state) => {
      state.success = false;
    },
    setCurrentPlatform: (state, action) => {
      state.currentPlatform = action.payload;
    },
    clearCurrentPlatform: (state) => {
      state.currentPlatform = null;
    },
    invalidateCache: (state) => {
      state.cached = false;
      state.lastFetched = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Get all platforms
      .addCase(getPlatforms.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getPlatforms.fulfilled, (state, action) => {
        // Silent operation
        state.loading = false;
        // Handle both cached and fresh data responses
        if (action.payload && action.payload.data) {
          state.platforms = action.payload.data;
        } else {
          state.platforms = action.payload;
        }
        state.cached = true;
        state.lastFetched = Date.now();
        state.error = null;
        // Don't set success for GET operations - only for CRUD operations
        // state.success should remain unchanged
      })
      .addCase(getPlatforms.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Get single platform
      .addCase(getPlatform.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getPlatform.fulfilled, (state, action) => {
        state.loading = false;
        state.currentPlatform = action.payload;
      })
      .addCase(getPlatform.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Create platform
      .addCase(createPlatform.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(createPlatform.fulfilled, (state, action) => {
        state.loading = false;
        state.platforms.push(action.payload);
        state.success = true;
        // Invalidate cache since data changed
        state.cached = false;
        state.lastFetched = null;
      })
      .addCase(createPlatform.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.success = false;
      })

      // Update platform
      .addCase(updatePlatform.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(updatePlatform.fulfilled, (state, action) => {
        state.loading = false;
        state.platforms = state.platforms.map(platform =>
          platform.id === action.payload.id ? action.payload : platform
        );
        state.currentPlatform = action.payload;
        state.success = true;
        // Invalidate cache since data changed
        state.cached = false;
        state.lastFetched = null;
      })
      .addCase(updatePlatform.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.success = false;
      })

      // Delete platform
      .addCase(deletePlatform.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(deletePlatform.fulfilled, (state, action) => {
        state.loading = false;
        state.platforms = state.platforms.filter(platform => platform.id !== action.payload);
        state.success = true;
        // Invalidate cache since data changed
        state.cached = false;
        state.lastFetched = null;
      })
      .addCase(deletePlatform.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.success = false;
      });
  },
});

export const {
  clearError,
  clearSuccess,
  setCurrentPlatform,
  clearCurrentPlatform,
  invalidateCache,
} = platformsSlice.actions;

export default platformsSlice.reducer;